<script lang="ts">
  import { fade } from 'svelte/transition';
  import { getStoresContext, type StoresKey } from "~/stores";
  import { onMount } from 'svelte';
  import { hrefFor, languageLabels, languageNames } from "./header-utils";

  export let request_store_key: StoresKey;
  export let enableHeaderLanguageSelectorMobile: boolean = false;
  export let maxLanguagesInline: string = "3";

  const stores_context = getStoresContext(request_store_key);
  const { locale, alternate, reverse_router, site } = stores_context;

  $: supportedLanguages = $site?.supportedLanguages || [];
  
  // Parse maxLanguagesInline and determine if we should use dropdown
  $: maxLanguagesThreshold = parseInt(maxLanguagesInline) || 3;
  $: shouldUseDropdown = supportedLanguages.length > maxLanguagesThreshold;
  $: currentLanguageLabel = languageLabels[$locale] || $locale?.slice(0, 2).toUpperCase();

  // Setup reactive viewport detection
  let isMobileView = false;
  let isLanguageDropdownOpen = false;
  let languageDropdownRef: HTMLElement | undefined;

  const browser = typeof window !== 'undefined';

  // Reactive viewport detection
  function updateViewport() {
    if (browser) {
      isMobileView = window.innerWidth < 992;
    }
  }

  $: if (browser) {
    updateViewport();
  }

  function toggleLanguageDropdown(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    
    if (isLanguageDropdownOpen) {
      closeLanguageDropdown();
    } else {
      isLanguageDropdownOpen = true;
    }
  }

  function closeLanguageDropdown() {
    isLanguageDropdownOpen = false;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isLanguageDropdownOpen) {
      closeLanguageDropdown();
    }
  }

  onMount(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isLanguageDropdownOpen && languageDropdownRef && !languageDropdownRef.contains(event.target as Node)) {
        isLanguageDropdownOpen = false;
      }
    };

    // Setup resize handler for viewport detection
    const handleResize = () => {
      updateViewport();
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleResize);
    
    // Initial viewport detection
    updateViewport();
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('resize', handleResize);
    };
  });
</script>

<!-- Desktop Language Selector -->
{#if supportedLanguages.length > 1 && !isMobileView}
  <div class="header-language-selector desktop" bind:this={languageDropdownRef}>
    {#if shouldUseDropdown}
      <!-- Dropdown mode -->
      <button 
        class="language-dropdown-toggle" 
        on:click={toggleLanguageDropdown}
        aria-label="Select language"
        aria-expanded={isLanguageDropdownOpen}
      >
        {currentLanguageLabel}
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      {#if isLanguageDropdownOpen}
        <div class="language-dropdown" transition:fade={{ duration: 150 }}>
          {#each supportedLanguages as langCode}
            {#if langCode === $locale}
              <span class="language-option active">
                {languageNames[langCode] || langCode}
              </span>
            {:else}
              <a 
                class="language-option" 
                href={hrefFor(langCode, $alternate, $reverse_router)}
                on:click={closeLanguageDropdown}
                aria-label={`Switch to ${languageNames[langCode] || langCode}`}
              >
                {languageNames[langCode] || langCode}
              </a>
            {/if}
          {/each}
        </div>
      {/if}
    {:else}
      <!-- Inline mode -->
      {#each supportedLanguages as langCode}
        {#if langCode === $locale}
          <span class="language-toggle active">
            {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
          </span>
        {:else}
          <a 
            class="language-toggle" 
            href={hrefFor(langCode, $alternate, $reverse_router)}
            aria-label={`Switch to ${langCode}`}
          >
            {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
          </a>
        {/if}
      {/each}
    {/if}
  </div>
{/if}

<!-- Mobile Language Selector -->
{#if enableHeaderLanguageSelectorMobile && supportedLanguages.length > 1 && isMobileView}
  <div class="header-language-selector mobile">
    {#if shouldUseDropdown}
      <!-- Dropdown mode -->
      <button 
        class="language-dropdown-toggle" 
        on:click={toggleLanguageDropdown}
        aria-label="Select language"
        aria-expanded={isLanguageDropdownOpen}
      >
        {currentLanguageLabel}
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      {#if isLanguageDropdownOpen}
        <div class="language-dropdown" transition:fade={{ duration: 150 }}>
          {#each supportedLanguages as langCode}
            {#if langCode === $locale}
              <span class="language-option active">
                {languageNames[langCode] || langCode}
              </span>
            {:else}
              <a 
                class="language-option" 
                href={hrefFor(langCode, $alternate, $reverse_router)}
                on:click={closeLanguageDropdown}
                aria-label={`Switch to ${languageNames[langCode] || langCode}`}
              >
                {languageNames[langCode] || langCode}
              </a>
            {/if}
          {/each}
        </div>
      {/if}
    {:else}
      <!-- Inline mode -->
      {#each supportedLanguages as langCode}
        {#if langCode === $locale}
          <span class="language-toggle active">
            {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
          </span>
        {:else}
          <a 
            class="language-toggle" 
            href={hrefFor(langCode, $alternate, $reverse_router)}
            aria-label={`Switch to ${langCode}`}
          >
            {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
          </a>
        {/if}
      {/each}
    {/if}
  </div>
{/if}

<style lang="scss">
  .header-language-selector {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-right: 0.5rem;
    position: relative;

    .language-toggle {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      text-transform: uppercase;
      color: var(--nav-link-color);
      transition: opacity 0.2s ease;
      font-size: var(--language-dropdown-font-size);
      font-weight: var(--language-dropdown-font-weight);
      letter-spacing: 0.02em;
      position: relative;
      text-decoration: none;
      display: inline-block;

      &.active {
        font-weight: 600;
        cursor: default;
        color: var(--nav-link-active-color);
        
        &::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          background-color: currentColor;
          border-radius: 50%;
        }
      }

      &:hover:not(.active) {
        opacity: 0.7;
      }
    }

    .language-dropdown-toggle {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      text-transform: uppercase;
      color: var(--nav-link-color);
      transition: opacity 0.2s ease;
      font-size: var(--language-dropdown-font-size);
      font-weight: var(--language-dropdown-font-weight);
      letter-spacing: 0.02em;
      display: flex;
      align-items: center;
      gap: 0.375rem;

      svg {
        transition: transform 0.2s ease;
        transform: rotate(0deg);
      }

      &[aria-expanded="true"] svg {
        transform: rotate(180deg);
      }

      &:hover {
        opacity: 0.7;
      }
    }

    .language-dropdown {
      position: absolute;
      top: calc(100% + var(--header-padding, 18px) + 0.25rem + var(--header-border-bottom-thickness, 1px) + 0.375rem);
      right: 0;
      background: var(--language-dropdown-inactive-bg-color);
      border: 1px solid var(--header-border-bottom-color);
      border-radius: var(--language-dropdown-border-radius);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 200;
      min-width: var(--language-dropdown-min-width);
      overflow: hidden;

      .language-option {
        display: block;
        padding: 0.5rem 0.75rem;
        text-decoration: none;
        color: var(--language-dropdown-inactive-color);
        background-color: var(--language-dropdown-inactive-bg-color);
        font-size: var(--language-dropdown-font-size);
        font-weight: var(--language-dropdown-font-weight);
        text-transform: uppercase;
        letter-spacing: 0.02em;
        transition: all 0.2s ease;
        border-bottom: 1px solid var(--header-border-bottom-color);

        &:last-child {
          border-bottom: none;
        }

        &.active {
          background-color: var(--language-dropdown-active-bg-color);
          color: var(--language-dropdown-active-color);
          font-weight: 600;
        }

        &:hover:not(.active) {
          background-color: var(--language-dropdown-hover-bg-color);
          color: var(--language-dropdown-hover-color);
        }
      }
    }
  }
</style>